// WS + ws-resume for admin structure change requests (roster/calendar pattern)

import { useCallback, useEffect, useRef } from 'react';
import { orgStructureChangeRequestsSocket } from '../../utils/Socket';
import { resumeAll, registerSocket } from '../wsRecovery/wsResume';
import { setResumePointer, updateResumeSeq } from '../wsRecovery/wsResumeStore';
import { startHeartbeat, stopHeartbeat } from '../wsRecovery/heartbeat';
import {
  getOrgStructureChangeRequestsWsDiff,
  orgStructureChangeRequestsWsLastSeen,
  ORG_STRUCTURE_CHANGE_REQUESTS_WS_CHANNEL,
  wsResumeOrgStructureChangeRequests,
} from '../pages/private/admin/organisationMembersTabs/requests/structureChangeRequestsApi';
import {
  fetchPendingCountOnce,
  invalidateStructureChangeRequestsList,
  setPendingStructureChangeRequestsCount,
} from './structureChangeRequestsWsStore';

const RESUME_REGISTRY_KEY = 'orgStructureChangeRequests';

let socketListenerCount = 0;
let resumeHandlerRegistered = false;

function applyPendingFromPayload(payload) {
  if (payload && typeof payload.pendingCount === 'number') {
    setPendingStructureChangeRequestsCount(payload.pendingCount);
    return true;
  }
  return false;
}

export default function useStructureChangeRequestsSocket({ enabled = true, isLoggedIn = true } = {}) {
  const lastResumeIdRef = useRef(0);

  const syncPointer = useCallback(async (wsLogId) => {
    const seq = Number(wsLogId || 0);
    if (seq > 0) {
      await orgStructureChangeRequestsWsLastSeen(seq).catch(() => {});
      lastResumeIdRef.current = Math.max(lastResumeIdRef.current, seq);
      updateResumeSeq(ORG_STRUCTURE_CHANGE_REQUESTS_WS_CHANNEL, seq);
    }
  }, []);

  const handleWsPayload = useCallback(
    async (payload, { invalidateList = true } = {}) => {
      const wsLogId = Number(payload?.wsLogId || 0);
      if (wsLogId > 0) {
        lastResumeIdRef.current = Math.max(lastResumeIdRef.current, wsLogId);
      }

      if (!applyPendingFromPayload(payload)) {
        await fetchPendingCountOnce();
      }

      if (invalidateList) {
        invalidateStructureChangeRequestsList();
      }

      if (wsLogId > 0) {
        await syncPointer(wsLogId);
      }
    },
    [syncPointer],
  );

  const runWsResume = useCallback(async () => {
    try {
      const data = await wsResumeOrgStructureChangeRequests(lastResumeIdRef.current);
      if (!data?.ok || !data.events?.length) return;

      const maxSeq = Math.max(...data.events.map((e) => Number(e.seq) || 0), 0);
      const lastEvent = data.events[data.events.length - 1];
      const pendingFromMeta = lastEvent?.meta?.pendingCount;

      if (typeof pendingFromMeta === 'number') {
        setPendingStructureChangeRequestsCount(pendingFromMeta);
      } else {
        await fetchPendingCountOnce();
      }

      invalidateStructureChangeRequestsList();

      if (maxSeq > 0) {
        lastResumeIdRef.current = Math.max(lastResumeIdRef.current, maxSeq);
        await syncPointer(maxSeq);
      }
    } catch (err) {
      console.error('[OrgStructureChangeRequests] ws-resume failed:', err);
    }
  }, [syncPointer]);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return undefined;

    setResumePointer(ORG_STRUCTURE_CHANGE_REQUESTS_WS_CHANNEL, {});

    if (!resumeHandlerRegistered) {
      resumeHandlerRegistered = true;
      registerSocket(RESUME_REGISTRY_KEY, async () => {
        if (!orgStructureChangeRequestsSocket.connected) {
          orgStructureChangeRequestsSocket.connect();
          startHeartbeat(orgStructureChangeRequestsSocket);
        }
        await runWsResume();
        invalidateStructureChangeRequestsList();
      });
    }

    return undefined;
  }, [enabled, isLoggedIn, runWsResume]);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return undefined;

    if (!orgStructureChangeRequestsSocket.connected) {
      orgStructureChangeRequestsSocket.connect();
      startHeartbeat(orgStructureChangeRequestsSocket);
    }

    const onConnect = () => {
      orgStructureChangeRequestsSocket.emit('joinOrgStructureChangeRequests');
      resumeAll();
    };

    const onReconnect = async () => {
      orgStructureChangeRequestsSocket.emit('joinOrgStructureChangeRequests');
      try {
        const diff = await getOrgStructureChangeRequestsWsDiff();
        if (diff?.needRefresh) {
          const lastEvent = Array.isArray(diff.events) ? diff.events[diff.events.length - 1] : null;
          if (lastEvent?.meta && typeof lastEvent.meta.pendingCount === 'number') {
            setPendingStructureChangeRequestsCount(lastEvent.meta.pendingCount);
          } else {
            await fetchPendingCountOnce();
          }
          invalidateStructureChangeRequestsList();
          if (diff.lastWsLogId) {
            await syncPointer(diff.lastWsLogId);
          }
        } else if (diff?.maxWsLogSeq) {
          lastResumeIdRef.current = Math.max(
            lastResumeIdRef.current,
            Number(diff.maxWsLogSeq),
          );
        }
      } catch (err) {
        console.error('[OrgStructureChangeRequests] reconnect diff failed:', err);
      }
    };

    orgStructureChangeRequestsSocket.on('connect', onConnect);
    orgStructureChangeRequestsSocket.on('reconnect', onReconnect);

    return () => {
      stopHeartbeat(orgStructureChangeRequestsSocket);
      orgStructureChangeRequestsSocket.off('connect', onConnect);
      orgStructureChangeRequestsSocket.off('reconnect', onReconnect);
    };
  }, [enabled, isLoggedIn, syncPointer]);

  useEffect(() => {
    const handler = () => {
      if (!enabled || !isLoggedIn) return;
      if (!orgStructureChangeRequestsSocket.connected) {
        orgStructureChangeRequestsSocket.connect();
        startHeartbeat(orgStructureChangeRequestsSocket);
      }
    };
    window.addEventListener('auth-ws-refreshed', handler);
    return () => window.removeEventListener('auth-ws-refreshed', handler);
  }, [enabled, isLoggedIn]);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return undefined;

    const resyncIfStale = async () => {
      try {
        const diff = await getOrgStructureChangeRequestsWsDiff();
        if (diff?.needRefresh) {
          const lastEvent = Array.isArray(diff.events) ? diff.events[diff.events.length - 1] : null;
          if (lastEvent?.meta && typeof lastEvent.meta.pendingCount === 'number') {
            setPendingStructureChangeRequestsCount(lastEvent.meta.pendingCount);
          } else {
            await fetchPendingCountOnce();
          }
          invalidateStructureChangeRequestsList();
        }
      } catch (err) {
        console.error('[OrgStructureChangeRequests] visibility/online diff failed:', err);
      }
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      resyncIfStale();
    };

    const onOnline = () => {
      resyncIfStale();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
    };
  }, [enabled, isLoggedIn]);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return undefined;

    socketListenerCount += 1;

    if (!orgStructureChangeRequestsSocket.connected) {
      orgStructureChangeRequestsSocket.connect();
      startHeartbeat(orgStructureChangeRequestsSocket);
    }

    orgStructureChangeRequestsSocket.emit('joinOrgStructureChangeRequests');

    const onMissed = async (payload) => {
      try {
        await handleWsPayload(payload || {}, { invalidateList: true });
      } catch (err) {
        console.error('[OrgStructureChangeRequests] missed handler failed:', err);
      }
    };

    const onUpdate = async (payload) => {
      try {
        await handleWsPayload(payload || {}, { invalidateList: true });
      } catch (err) {
        console.error('[OrgStructureChangeRequests] update handler failed:', err);
      }
    };

    orgStructureChangeRequestsSocket.on('orgStructureChangeRequestsMissed', onMissed);
    orgStructureChangeRequestsSocket.on('orgStructureChangeRequestsUpdate', onUpdate);

    return () => {
      orgStructureChangeRequestsSocket.off('orgStructureChangeRequestsMissed', onMissed);
      orgStructureChangeRequestsSocket.off('orgStructureChangeRequestsUpdate', onUpdate);
      socketListenerCount -= 1;
      if (socketListenerCount === 0) {
        orgStructureChangeRequestsSocket.emit('leaveOrgStructureChangeRequests');
      }
    };
  }, [enabled, isLoggedIn, handleWsPayload]);

  return { lastResumeIdRef };
}
